import React, { useEffect, useMemo, useState } from 'react';
import Alert from '../../../shared/components/Alert/Alert';
import CircularProgress from '../../../shared/components/CircularProgress/CircularProgress';
import { getProjectPlanningTraceability } from '../project.api';

const formatHours = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  const number = Number(value);
  if (Number.isNaN(number)) return '—';
  return `${number.toLocaleString('fr-FR', { minimumFractionDigits: number % 1 ? 1 : 0, maximumFractionDigits: 1 })} h`;
};

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleDateString('fr-FR');
};

const getDelta = (current, baseline) => {
  if (current === null || current === undefined || baseline === null || baseline === undefined) return null;
  const delta = Number(current) - Number(baseline);
  return Number.isFinite(delta) ? delta : null;
};

const Delta = ({ current, baseline, suffix = ' h' }) => {
  const delta = getDelta(current, baseline);
  if (delta === null || Math.abs(delta) < 0.001) return null;
  const sign = delta > 0 ? '+' : '';
  return <small className={`project-trace-delta ${delta > 0 ? 'is-negative' : 'is-positive'}`}>{sign}{delta.toLocaleString('fr-FR', { maximumFractionDigits: 1 })}{suffix}</small>;
};

const StageValue = ({ title, hours, startDate, endDate, muted = false }) => (
  <div className={`project-trace-stage ${muted ? 'is-muted' : ''}`}>
    <span>{title}</span>
    <strong>{formatHours(hours)}</strong>
    {(startDate || endDate) && <small>{formatDate(startDate)} → {formatDate(endDate)}</small>}
  </div>
);

function ProjectPlanningTraceability({ projectId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let active = true;
    const load = async ({ silent = false } = {}) => {
      try {
        if (!silent) setLoading(true);
        const result = await getProjectPlanningTraceability(projectId);
        if (active) {
          setData(result);
          setError(null);
        }
      } catch (err) {
        if (active) setError(err.message || 'Impossible de charger la traçabilité du planning.');
      } finally {
        if (active && !silent) setLoading(false);
      }
    };
    load();
    const refresh = () => load({ silent: true });
    window.addEventListener('focus', refresh);
    window.addEventListener('time-entry-updated', refresh);
    return () => {
      active = false;
      window.removeEventListener('focus', refresh);
      window.removeEventListener('time-entry-updated', refresh);
    };
  }, [projectId]);

  const summary = data?.summary || {};
  const rows = useMemo(() => Array.isArray(data?.rows) ? data.rows : [], [data]);

  if (loading) return <div className="project-trace-loading"><CircularProgress size={22} /> Chargement de la référence planning…</div>;
  if (error) return <Alert severity="warning">{error}</Alert>;
  if (!rows.length) return <div className="project-trace-empty">Aucune référence de planning figée n'est disponible pour ce projet.</div>;

  return (
    <div className="project-traceability">
      <div className="project-traceability__header">
        <div>
          <h3>Traçabilité du chiffrage et du planning</h3>
          <p>Comparaison entre ce qui a été chiffré, la référence figée à la création du projet et la LDA actuellement pilotée.</p>
        </div>
        <button className="basic-button" type="button" onClick={() => setExpanded((value) => !value)}>
          {expanded ? 'Masquer le détail' : 'Voir le détail'}
        </button>
      </div>

      <div className="project-trace-summary">
        <StageValue title="Chiffré" hours={summary.quotedHours} />
        <StageValue title="Référence démarrage" hours={summary.baselineHours} />
        <div className="project-trace-stage">
          <span>Prévision actuelle</span>
          <strong>{formatHours(summary.currentHours)}</strong>
          <Delta current={summary.currentHours} baseline={summary.baselineHours} />
        </div>
        <div className="project-trace-stage">
          <span>Réalisé</span>
          <strong>{formatHours(summary.actualHours)}</strong>
          <small>Temps total pointé sur le projet</small>
          {Number(summary.unallocatedActualHours || 0) > 0 && <small>{formatHours(summary.unallocatedActualHours)} non rattachée(s) à un livrable</small>}
        </div>
      </div>

      <div className="project-trace-meta">
        <span>{summary.baselineItemCount || 0} livrable(s) de référence</span>
        <span>{summary.modifiedItemCount || 0} ajusté(s) depuis le démarrage</span>
        <span>{summary.addedItemCount || 0} ajouté(s) en cours de projet</span>
        {data.acceptedQuoteVersionId && <span>Version devis #{data.acceptedQuoteVersionId}</span>}
      </div>

      {expanded && (
        <div className="project-trace-table-wrap">
          <table className="project-trace-table">
            <thead>
              <tr>
                <th>Livrable</th>
                <th>Chiffré</th>
                <th>Référence démarrage</th>
                <th>Prévision actuelle</th>
                <th>Réalisé</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.planningSourceKey || `extra-${row.id}`} className={row.origin === 'project_addition' ? 'is-added' : ''}>
                  <td>
                    <strong>{row.title}</strong>
                    <small>{[row.phaseCode, row.lotCode, row.levelCode].filter(Boolean).join(' · ') || 'Livrable projet'}</small>
                    {row.origin === 'project_addition' && <span className="project-trace-badge">Ajout projet</span>}
                  </td>
                  <td><strong>{formatHours(row.quotedHours)}</strong><small>{formatDate(row.quotedStartDate)} → {formatDate(row.quotedEndDate)}</small></td>
                  <td><strong>{formatHours(row.baselineHours)}</strong><small>{formatDate(row.baselineStartDate)} → {formatDate(row.baselineEndDate)}</small></td>
                  <td>
                    <strong>{formatHours(row.currentHours)}</strong>
                    <small>{formatDate(row.currentStartDate)} → {formatDate(row.currentEndDate)}</small>
                    <Delta current={row.currentHours} baseline={row.baselineHours} />
                  </td>
                  <td><strong>{formatHours(row.actualHours)}</strong><small>{row.currentStatus || '—'}</small></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default ProjectPlanningTraceability;
